import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, CreditCard, ChevronDown, ChevronUp, AlertCircle, Bus } from 'lucide-react';
import { RouteService } from '../../services/routeService';
import { ScheduleService } from '../../services/scheduleService';
import { Route, BusSchedule } from '../../types';

export function RoutesSchedules() {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [schedules, setSchedules] = useState<BusSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRouteId, setExpandedRouteId] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const activeRoutes = await RouteService.getActiveRoutes();
        setRoutes(activeRoutes);
      } catch (error) {
        console.error('Failed to fetch routes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Subscribe to live schedules
    const unsubscribe = ScheduleService.subscribeToSchedules((liveSchedules) => {
      // Filter for upcoming and active schedules
      const relevantSchedules = liveSchedules.filter(s => 
        s.status === 'scheduled' || s.status === 'active'
      );
      setSchedules(relevantSchedules);
    });

    return () => unsubscribe();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-brand-orange text-white';
      case 'scheduled':
        return 'bg-blue-100 text-brand-blue';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-16 bg-secondary-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16 bg-secondary-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-extrabold text-secondary-900 mb-4 tracking-tight">
            Routes & Live Schedules
          </h1>
          <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
            Find your perfect journey. View our active routes, competitive pricing, and real-time bus departures.
          </p>
        </motion.div>

        <div className="space-y-6">
          {routes.length === 0 ? (
            <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-secondary-100">
              <AlertCircle className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
              <p className="text-secondary-600 font-medium">No active routes found at this time.</p>
            </div>
          ) : (
            routes.map((route, index) => {
              const routeSchedules = schedules.filter(s => s.routeId === route.id);
              const isExpanded = expandedRouteId === route.id;

              return (
                <motion.div
                  key={route.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-sm border border-secondary-100 overflow-hidden"
                >
                  {/* Route Header (Clickable) */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-secondary-50 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-4"
                    onClick={() => setExpandedRouteId(isExpanded ? null : route.id)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-brand-blue shrink-0">
                          <MapPin className="w-5 h-5" />
                        </div>
                        <h2 className="text-2xl font-bold text-secondary-900">
                          {route.fromCity} <span className="text-secondary-400 font-normal mx-2">→</span> {route.toCity}
                        </h2>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-secondary-600 pl-[3.25rem]">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-secondary-400" />
                          <span>{Math.floor(route.duration / 60)}h {route.duration % 60}m</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <CreditCard className="w-4 h-4 text-secondary-400" />
                          <span className="font-semibold text-brand-blue">
                            Base: GHS {route.price?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between md:justify-end gap-6 border-t md:border-t-0 pt-4 md:pt-0 border-secondary-100">
                      <div className="text-left md:text-right">
                        <p className="text-sm font-medium text-secondary-500 mb-1">Live Departures</p>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-orange/10 text-brand-orange font-bold text-xs">
                            {routeSchedules.length}
                          </span>
                          <span className="text-sm text-secondary-600">upcoming</span>
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-500">
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Schedules Section */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-secondary-100 bg-secondary-50 p-6"
                    >
                      <h3 className="text-sm font-bold text-secondary-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <Bus className="w-4 h-4 text-brand-blue" />
                        Live Schedule Board
                      </h3>
                      
                      {routeSchedules.length === 0 ? (
                        <p className="text-sm text-secondary-500 italic">No upcoming departures scheduled for this route.</p>
                      ) : (
                        <div className="grid gap-3">
                          {routeSchedules.map((schedule) => (
                            <div key={schedule.id} className="bg-white border border-secondary-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-brand-blue/30 transition-colors shadow-sm">
                              <div>
                                <div className="flex items-center gap-3 mb-1">
                                  <span className="text-lg font-bold text-secondary-900">
                                    {schedule.departureTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  <span className="text-secondary-400">→</span>
                                  <span className="text-lg font-bold text-secondary-600">
                                    {schedule.arrivalTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <div className="text-sm text-secondary-500 flex items-center gap-2">
                                  <span>{schedule.departureTime.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                                  <span>•</span>
                                  <span className="font-medium text-secondary-700">{schedule.busNumber || 'TBD'}</span>
                                  {schedule.busType && (
                                    <>
                                      <span>•</span>
                                      <span className="text-xs bg-secondary-100 px-2 py-0.5 rounded text-secondary-600">{schedule.busType}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center justify-between sm:justify-end gap-4 border-t sm:border-t-0 pt-3 sm:pt-0 border-secondary-100">
                                <div className="text-sm">
                                  <span className="text-secondary-500 mr-1">Seats:</span>
                                  <span className="font-semibold text-secondary-900">{schedule.availableSeats || 0}</span>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getStatusColor(schedule.status)}`}>
                                  {schedule.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
